import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Clock3,
  Globe2,
  Plane,
  Route,
  ShieldCheck,
  Ship,
  Smartphone,
} from 'lucide-react';
import { getProductBySlug } from '@/data/products';

const campaigns = {
  '/travel/flying-abroad': {
    id: 'flying_abroad',
    icon: Plane,
    eyebrow: 'FLYING ABROAD?',
    heading: 'Land. Connect. Go.',
    description: 'Travel eSIM data and short-term VPN access for your trip, without another monthly contract.',
    intro: 'Choose a one-off travel pack before you fly. Use mobile data in supported destinations and protect traffic carried through your VPN tunnel on airport, hotel and public Wi-Fi.',
    accent: '#00ffc8',
    primaryTarget: '/#travel-packs',
    primaryLabel: 'Choose travel data',
    benefits: [
      ['Get connected when you land', 'Travel eSIM data for supported destinations and compatible, unlocked devices.'],
      ['Protect traffic on shared Wi-Fi', 'Short-term VPN access is available separately or bundled in a Privacy Pack.'],
      ['Buy only what your trip needs', 'Choose a one-off 7, 14 or 30-day option with no automatic renewal.'],
    ],
    productSlugs: ['travel-esim-5gb-1-week', 'travel-esim-10gb-2-week', 'privacy-pack-10gb-2-week'],
    note: 'Coverage depends on the selected destination, local mobile networks and device compatibility. A VPN protects traffic carried through its encrypted tunnel but does not guarantee access to any third-party app, bank or streaming service.',
  },
  '/travel/ferry-and-cruise': {
    id: 'ferry_and_cruise',
    icon: Ship,
    eyebrow: 'SETTING SAIL?',
    heading: 'Cross borders. Keep control of your connection.',
    description: 'Travel eSIM data and short-term VPN access for ports, destinations and time ashore.',
    intro: 'Prepare before you leave with a one-off travel pack. Connect in supported destinations and use VPN protection on port, hotel, café and other shared Wi-Fi networks.',
    accent: '#38bdf8',
    primaryTarget: '/#travel-packs',
    primaryLabel: 'Choose your travel pack',
    benefits: [
      ['Data for supported destinations', 'Choose the allowance and duration that suits your route.'],
      ['VPN access when you need it', 'Protect traffic carried through your tunnel on shared networks during the trip.'],
      ['No monthly commitment', 'Every pack is a one-off purchase with no automatic renewal.'],
    ],
    productSlugs: ['travel-esim-5gb-1-week', 'travel-esim-10gb-2-week', 'privacy-pack-20gb-30-day'],
    note: 'Travel eSIM service uses ordinary terrestrial mobile networks in supported destinations. Maritime or satellite connectivity while a ship is at sea is not included. Coverage, speed and availability depend on local networks and device compatibility.',
  },
  '/travel/on-the-road': {
    id: 'on_the_road',
    icon: Route,
    eyebrow: 'GOING SOMEWHERE?',
    heading: 'Take your connection with you.',
    description: 'One-off travel eSIM data, short-term VPN access and optional UK or USA virtual numbers.',
    intro: 'Whether you are heading to an airport, ferry port, hotel or another country, choose only the connectivity tools you need for the journey.',
    accent: '#2dd4bf',
    primaryTarget: '/#privacy-packs',
    primaryLabel: 'Explore travel and privacy packs',
    benefits: [
      ['Travel data without a subscription', 'Use a compatible eSIM in supported destinations for 7, 14 or 30 days.'],
      ['Short-term VPN protection', 'Choose VPN access separately or together with travel data in a Privacy Pack.'],
      ['Optional virtual numbers', 'UK and USA numbers are available for supported general calls and messaging.'],
    ],
    productSlugs: ['travel-esim-10gb-2-week', 'privacy-pack-10gb-2-week', 'privacy-pack-20gb-30-day'],
    note: 'Destination coverage and device compatibility apply. Virtual-number availability, calls, SMS delivery and third-party platform compatibility are not guaranteed. PhantomPath virtual numbers do not support emergency calling.',
  },
};

const trustPoints = [
  [Clock3, 'One-off access'],
  [ShieldCheck, 'No subscriptions'],
  [Smartphone, 'No mandatory customer account'],
];

const TravelCampaignPage = () => {
  const { pathname } = useLocation();
  const campaign = campaigns[pathname];
  const Icon = campaign.icon;
  const products = campaign.productSlugs.map((slug) => getProductBySlug(slug)).filter(Boolean);
  const canonical = `https://phantompathvpn.com${pathname}`;

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#030706] px-4 pb-20 pt-28 text-white">
      <Helmet>
        <title>{campaign.eyebrow} {campaign.heading} | PhantomPathVPN</title>
        <meta name="description" content={campaign.description} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${campaign.eyebrow} ${campaign.heading}`} />
        <meta property="og:description" content={campaign.description} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-24 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full opacity-15 blur-[110px]" style={{ backgroundColor: campaign.accent }} />
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)', backgroundSize: '42px 42px' }} />
      </div>

      <div className="relative mx-auto max-w-5xl">
        <section className="pb-12 text-center" data-ga-location={`travel_campaign_${campaign.id}_hero`}>
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-black/40 backdrop-blur-sm">
            <Icon className="h-7 w-7" style={{ color: campaign.accent }} aria-hidden="true" />
          </span>
          <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.28em]" style={{ color: campaign.accent }}>{campaign.eyebrow}</p>
          <h1 className="mx-auto mt-3 max-w-4xl text-4xl font-black leading-[1.08] sm:text-6xl">{campaign.heading}</h1>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-gray-200 sm:text-lg">{campaign.intro}</p>
          <Link
            to={campaign.primaryTarget}
            data-ga-name={`campaign_${campaign.id}_primary_cta`}
            className="mt-7 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-extrabold text-[#03110d] transition hover:brightness-110"
            style={{ backgroundColor: campaign.accent }}
          >
            {campaign.primaryLabel}<ArrowRight className="h-4 w-4" />
          </Link>

          <div className="mx-auto mt-7 grid max-w-3xl gap-2 sm:grid-cols-3" aria-label="Purchase benefits">
            {trustPoints.map(([TrustIcon, label]) => (
              <div key={label} className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/35 px-3 py-3 text-xs font-bold text-gray-100 backdrop-blur-sm">
                <TrustIcon className="h-4 w-4" style={{ color: campaign.accent }} />{label}
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-3 border-y border-white/10 py-10 md:grid-cols-3" aria-label="Why choose this travel option">
          {campaign.benefits.map(([title, copy]) => (
            <article key={title} className="rounded-2xl border border-white/10 bg-white/[0.045] p-5 backdrop-blur-sm">
              <Check className="h-5 w-5" style={{ color: campaign.accent }} />
              <h2 className="mt-4 text-lg font-bold">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-gray-300">{copy}</p>
            </article>
          ))}
        </section>

        <section className="py-12" aria-labelledby={`${campaign.id}-packages`} data-ga-location={`travel_campaign_${campaign.id}_packages`}>
          <div className="text-center">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em]" style={{ color: campaign.accent }}>POPULAR ONE-OFF OPTIONS</p>
            <h2 id={`${campaign.id}-packages`} className="mt-3 text-3xl font-black">Choose what fits your journey</h2>
            <p className="mt-3 text-sm leading-6 text-gray-400">Select a package to view it on the main pricing page.</p>
          </div>
          <div className="mt-7 grid gap-4 md:grid-cols-3">
            {products.map((product) => (
              <Link
                key={product.slug}
                to={`/#${product.slug}`}
                data-ga-name={`campaign_${campaign.id}_${product.slug}`}
                className="group flex min-h-52 flex-col rounded-2xl border border-white/10 bg-[#0b0f0f]/90 p-5 transition hover:-translate-y-1 hover:border-white/25"
              >
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em]" style={{ color: product.accent }}>{product.categoryLabel}</p>
                <h3 className="mt-3 text-lg font-bold leading-6">{product.name}</h3>
                <p className="mt-2 text-sm leading-6 text-gray-400">{product.description}</p>
                <div className="mt-auto flex items-end justify-between pt-5">
                  <div><strong className="text-2xl">{product.price}</strong><p className="mt-1 text-xs text-gray-500">{product.duration}</p></div>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 transition group-hover:border-white/30"><ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <aside className="rounded-2xl border border-white/10 bg-black/35 p-5 backdrop-blur-sm" aria-label="Important travel information">
          <div className="flex items-start gap-3">
            <Globe2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: campaign.accent }} />
            <div><h2 className="font-bold">Before you travel</h2><p className="mt-2 text-xs leading-6 text-gray-400">{campaign.note}</p></div>
          </div>
        </aside>

        <section className="pb-4 pt-14 text-center" data-ga-location={`travel_campaign_${campaign.id}_final_cta`}>
          <h2 className="text-3xl font-black">Scan. Choose. Travel.</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-gray-300">No subscription. No automatic renewal. Buy only the access and data you need.</p>
          <Link
            to={campaign.primaryTarget}
            data-ga-name={`campaign_${campaign.id}_final_cta`}
            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-6 py-3.5 text-sm font-bold transition hover:border-white/30 hover:bg-white/[0.1]"
          >
            View travel options<ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </main>
  );
};

export default TravelCampaignPage;
